import type { ReactNode } from "react";
import { useI18n } from "../../i18n";
import { GlobalWorkspaceSwitcher, type Surface } from "./GlobalWorkspaceSwitcher";

export type WorkspaceView = "chat" | "members" | "computers";

interface Props {
  surface: Surface;
  onPickChat: () => void;
  onPickResearch: () => void;
  onPickWorkspace: (accountId: string) => void;
  onCreated: () => void;
  /** Workspace sub-nav (only shown on the workspace surface). */
  workspaceView: WorkspaceView;
  onWorkspaceView: (v: WorkspaceView) => void;
  onOpenSettings: () => void;
}

/**
 * The app's universal left icon rail (all surfaces): brand mark,
 * the workspace switcher (compact), the workspace sub-nav when inside a
 * workspace, and Settings at the bottom (opens the drawer). There is no top
 * header — this rail replaces it.
 */
export function AppRail({
  surface,
  onPickChat,
  onPickResearch,
  onPickWorkspace,
  onCreated,
  workspaceView,
  onWorkspaceView,
  onOpenSettings,
}: Props) {
  const { t } = useI18n();
  const subnav: { key: WorkspaceView; label: string; icon: ReactNode }[] = [
    { key: "chat", label: t("collab.channels"), icon: iconChat },
    { key: "members", label: t("collab.members"), icon: iconPeople },
    { key: "computers", label: t("collab.computers"), icon: iconMonitor },
  ];

  return (
    <nav className="ws-rail" aria-label={t("collab.workspace.title")}>
      <GlobalWorkspaceSwitcher
        rail
        surface={surface}
        onPickChat={onPickChat}
        onPickResearch={onPickResearch}
        onPickWorkspace={onPickWorkspace}
        onCreated={onCreated}
      />

      {surface === "workspace" ? (
        <div className="ws-rail-nav">
          {subnav.map((it) => (
            <button
              key={it.key}
              type="button"
              className={`ws-rail-btn${workspaceView === it.key ? " active" : ""}`}
              onClick={() => onWorkspaceView(it.key)}
              title={it.label}
              aria-label={it.label}
              aria-current={workspaceView === it.key}
            >
              {it.icon}
            </button>
          ))}
        </div>
      ) : null}

      <button type="button" className={`ws-rail-btn ws-rail-settings${surface === "settings" ? " active" : ""}`} onClick={onOpenSettings} title={t("collab.settings")} aria-label={t("collab.settings")}>
        {iconGear}
      </button>
    </nav>
  );
}

const iconChat = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.4 8.6 8.6 0 0 1-4-.9L3 20l1.1-4.9a8.4 8.4 0 0 1-.9-3.8A8.5 8.5 0 0 1 11.7 3h.3a8.4 8.4 0 0 1 9 8.5Z" />
  </svg>
);
const iconPeople = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const iconMonitor = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);
const iconGear = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);
